import React, { useState, useEffect, useCallback } from 'react';
import { 
  Video, 
  Plus, 
  Users, 
  Calendar as CalendarIcon, 
  Clock, 
  Edit, 
  Trash2, 
  Copy, 
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  PlayCircle,
  Link as LinkIcon,
  Eye
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from '../../hooks/use-toast';
import { format, parseISO } from 'date-fns';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

// Canadian Timezones + Turkey (Test) for dropdown
const CANADIAN_TIMEZONES = [
  { value: 'America/St_Johns', label: 'Newfoundland (NST/NDT)' },
  { value: 'America/Halifax', label: 'Atlantic (AST/ADT)' },
  { value: 'America/Toronto', label: 'Eastern (EST/EDT)' },
  { value: 'America/Winnipeg', label: 'Central (CST/CDT)' },
  { value: 'America/Regina', label: 'Saskatchewan (CST)' },
  { value: 'America/Edmonton', label: 'Mountain (MST/MDT)' },
  { value: 'America/Vancouver', label: 'Pacific (PST/PDT)' },
  { value: 'America/Whitehorse', label: 'Yukon (PST)' },
  // Test timezone - Turkey
  { value: 'Europe/Istanbul', label: '🧪 TEST: Turkey (TRT, UTC+3)' },
];

// Separate ClassForm component to avoid unstable nested components
const ClassForm = ({ formData, setFormData, onSubmit, onCancel, submitting, isEdit = false }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div>
      <Label htmlFor="title">Class Title *</Label>
      <Input
        id="title"
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        placeholder="e.g., Morning HIIT Session"
        required
        minLength={3}
        maxLength={200}
      />
    </div>

    <div>
      <Label htmlFor="short_description">Short Description * (shown on trainer profile)</Label>
      <Input
        id="short_description"
        value={formData.short_description}
        onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
        placeholder="Brief summary of the class"
        required
        minLength={10}
        maxLength={300}
      />
    </div>

    <div>
      <Label htmlFor="detailed_description">Detailed Description *</Label>
      <Textarea
        id="detailed_description"
        value={formData.detailed_description}
        onChange={(e) => setFormData({ ...formData, detailed_description: e.target.value })}
        placeholder="Detailed information about what participants can expect..."
        required
        minLength={20}
        maxLength={5000}
        rows={4}
      />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="scheduled_date">Date *</Label>
        <Input
          id="scheduled_date"
          type="date"
          value={formData.scheduled_date}
          onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
          required
          min={format(new Date(), 'yyyy-MM-dd')}
        />
      </div>
      <div>
        <Label htmlFor="start_time">Start Time *</Label>
        <Input
          id="start_time"
          type="time"
          value={formData.start_time}
          onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
          required
        />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <Label htmlFor="duration_minutes">Duration (minutes) *</Label>
        <Input
          id="duration_minutes"
          type="number"
          value={formData.duration_minutes}
          onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
          required
          min={15}
          max={480}
        />
      </div>
      <div>
        <Label htmlFor="max_participants">Max Participants *</Label>
        <Input
          id="max_participants"
          type="number"
          value={formData.max_participants}
          onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) || 20 })}
          required
          min={1}
          max={500}
        />
      </div>
    </div>

    <div>
      <Label htmlFor="class_type">Class Type *</Label>
      <Select
        value={formData.class_type}
        onValueChange={(value) => setFormData({ ...formData, class_type: value })}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="public">Public (visible on your profile)</SelectItem>
          <SelectItem value="private">Private (only accessible via link)</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div>
      <Label htmlFor="timezone">Timezone *</Label>
      <Select
        value={formData.trainer_timezone || 'America/Toronto'}
        onValueChange={(value) => setFormData({ ...formData, trainer_timezone: value })}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select timezone" />
        </SelectTrigger>
        <SelectContent>
          {CANADIAN_TIMEZONES.map((tz) => (
            <SelectItem key={tz.value} value={tz.value}>
              {tz.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-gray-500 mt-1">
        Class times will be shown in participants&apos; local timezone
      </p>
    </div>

    <DialogFooter>
      <Button type="button" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" disabled={submitting} className="bg-red-600 hover:bg-red-700">
        {submitting ? 'Saving...' : (isEdit ? 'Update Class' : 'Create Class')}
      </Button>
    </DialogFooter>
  </form>
);

const GroupClassesTab = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showActiveClassWarning, setShowActiveClassWarning] = useState(false);
  const [activeClassInfo, setActiveClassInfo] = useState(null);
  const [classToCancelId, setClassToCancelId] = useState(null);
  const [cancellingClass, setCancellingClass] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    detailed_description: '',
    scheduled_date: '',
    start_time: '',
    duration_minutes: 60,
    max_participants: 20,
    class_type: 'public',
    trainer_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto'
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchClasses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const url = filterStatus && filterStatus !== 'all'
        ? `${API_BASE}/api/trainer/group-classes?status=${filterStatus}`
        : `${API_BASE}/api/trainer/group-classes`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setClasses(data.classes || []);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  const resetForm = () => {
    setFormData({
      title: '',
      short_description: '',
      detailed_description: '',
      scheduled_date: '',
      start_time: '',
      duration_minutes: 60,
      max_participants: 20,
      class_type: 'public',
      trainer_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto'
    });
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      // Send form data with timezone for UTC conversion on backend
      const dataWithTimezone = {
        ...formData
      };
      
      const response = await fetch(`${API_BASE}/api/trainer/group-classes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataWithTimezone)
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Group class created successfully!' });
        setShowCreateModal(false);
        resetForm();
        fetchClasses();
      } else {
        const error = await response.json();
        
        // Check if it's an active class error
        if (error.detail?.code === 'ACTIVE_CLASS_EXISTS') {
          setActiveClassInfo(error.detail.active_class);
          setShowActiveClassWarning(true);
          setShowCreateModal(false);
        } else {
          toast({ title: 'Error', description: error.detail?.message || error.detail || 'Failed to create class', variant: 'destructive' });
        }
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create class', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateClass = async (e) => {
    e.preventDefault();
    if (!selectedClass) return;
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/group-classes/${selectedClass.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Class updated successfully!' });
        setShowEditModal(false);
        setSelectedClass(null);
        resetForm();
        fetchClasses();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to update class', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update class', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // Open cancel confirmation modal
  const openCancelModal = (classId) => {
    setClassToCancelId(classId);
    setShowCancelModal(true);
  };

  const handleCancelClass = async () => {
    if (!classToCancelId) return;
    
    setCancellingClass(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/group-classes/${classToCancelId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast({ 
          title: 'Class Cancelled', 
          description: data.emails_sent > 0 
            ? `Class cancelled. ${data.emails_sent} participant(s) have been notified.`
            : 'Class cancelled successfully.' 
        });
        setShowCancelModal(false);
        setClassToCancelId(null);
        fetchClasses();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to cancel class', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to cancel class', variant: 'destructive' });
    } finally {
      setCancellingClass(false);
    }
  };

  const fetchParticipants = async (classId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainer/group-classes/${classId}/participants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setParticipants(data.participants || []);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  };

  const openEditModal = (classItem) => {
    setSelectedClass(classItem);
    setFormData({
      title: classItem.title,
      short_description: classItem.short_description,
      detailed_description: classItem.detailed_description,
      scheduled_date: classItem.scheduled_date,
      start_time: classItem.start_time,
      duration_minutes: classItem.duration_minutes,
      max_participants: classItem.max_participants,
      class_type: classItem.class_type,
      trainer_timezone: classItem.trainer_timezone || 'America/Toronto'
    });
    setShowEditModal(true);
  };

  const openParticipantsModal = async (classItem) => {
    setSelectedClass(classItem);
    await fetchParticipants(classItem.id);
    setShowParticipantsModal(true);
  };

  const copyShareLink = (shareLink) => {
    navigator.clipboard.writeText(shareLink);
    toast({ title: 'Link Copied', description: 'Share link copied to clipboard!' });
  };

  const joinAsHost = (jitsiUrl) => {
    window.open(jitsiUrl, '_blank');
  };

  const getStatusBadge = (status) => {
    const styles = {
      upcoming: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: CalendarIcon },
      live: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: PlayCircle },
      completed: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-400', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: XCircle }
    };
    
    const style = styles[status] || styles.upcoming;
    const Icon = style.icon;
    
    return (
      <Badge className={`${style.bg} ${style.text} gap-1`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const canCancelClass = (classItem) => {
    // Can only cancel UPCOMING classes (not LIVE, COMPLETED, or CANCELLED)
    return classItem.status === 'upcoming';
  };

  const formatDateTime = (date, time, timezone) => {
    try {
      // Show time in trainer's timezone
      const dateTimeStr = `${date}T${time}:00`;
      const tz = timezone || 'America/Toronto';
      
      // Find timezone label
      const tzInfo = CANADIAN_TIMEZONES.find(t => t.value === tz);
      const tzLabel = tzInfo ? tzInfo.label.split(' ')[0] : tz.split('/')[1];
      
      return `${format(parseISO(dateTimeStr), 'PPP p')} (${tzLabel})`;
    } catch {
      return `${date} ${time}`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Video className="w-5 h-5" />
            Group Classes
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Create and manage online group classes via Jitsi Meet
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Class
          </Button>
        </div>
      </div>

      {/* Classes List */}
      {classes.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Video className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Group Classes Yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Create your first online group class and start engaging with multiple clients at once!
              </p>
              <Button onClick={() => setShowCreateModal(true)} className="bg-red-600 hover:bg-red-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Class
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {classes.map((classItem) => (
            <Card key={classItem.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4 sm:p-6">
                  {/* Header Row */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {classItem.title}
                        </h3>
                        {getStatusBadge(classItem.status)}
                        {classItem.class_type === 'private' && (
                          <Badge variant="outline" className="text-purple-600 border-purple-300">
                            Private
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {classItem.short_description}
                      </p>
                    </div>
                    
                    {/* Live Join Button */}
                    {classItem.status === 'live' && classItem.jitsi_room_url && (
                      <Button
                        onClick={() => joinAsHost(classItem.jitsi_room_url)}
                        className="bg-green-600 hover:bg-green-700 animate-pulse"
                      >
                        <PlayCircle className="w-4 h-4 mr-2" />
                        Join as Host
                      </Button>
                    )}
                  </div>

                  {/* Info Row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <CalendarIcon className="w-4 h-4" />
                      <span>{formatDateTime(classItem.scheduled_date, classItem.start_time, classItem.trainer_timezone)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Clock className="w-4 h-4" />
                      <span>{classItem.duration_minutes} min</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Users className="w-4 h-4" />
                      <span>{classItem.current_participants}/{classItem.max_participants} participants</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      {classItem.remaining_spots > 0 ? (
                        <span className="text-green-600 dark:text-green-400">
                          {classItem.remaining_spots} spots available
                        </span>
                      ) : (
                        <span className="text-red-600 dark:text-red-400">Class Full</span>
                      )}
                    </div>
                  </div>

                  {/* Progress Bar for capacity */}
                  <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-4">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        classItem.is_full ? 'bg-red-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${(classItem.current_participants / classItem.max_participants) * 100}%` }}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* View Participants */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openParticipantsModal(classItem)}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Participants ({classItem.current_participants})
                    </Button>

                    {/* Copy Share Link */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Generate correct frontend URL
                        const frontendUrl = `${window.location.origin}/group-class/${classItem.slug}`;
                        navigator.clipboard.writeText(frontendUrl);
                        toast({ title: 'Link Copied', description: 'Share link copied to clipboard!' });
                      }}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>

                    {/* View Class Page */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/group-class/${classItem.slug}`, '_blank')}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>

                    {/* Edit (only for upcoming) */}
                    {classItem.status === 'upcoming' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(classItem)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    )}

                    {/* Join as Host (upcoming or live) */}
                    {classItem.status === 'upcoming' && classItem.jitsi_room_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => joinAsHost(classItem.jitsi_room_url)}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Open Room
                      </Button>
                    )}

                    {/* Cancel (only for upcoming) */}
                    {canCancelClass(classItem) && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => openCancelModal(classItem.id)}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Cancel Class
                      </Button>
                    )}

                    {/* Status messages for non-cancellable classes */}
                    {classItem.status === 'live' && (
                      <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Cannot cancel live class
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Class Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5" />
              Create Group Class
            </DialogTitle>
          </DialogHeader>
          <ClassForm 
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleCreateClass}
            onCancel={() => { setShowCreateModal(false); resetForm(); }}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Class Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Group Class
            </DialogTitle>
          </DialogHeader>
          <ClassForm 
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleUpdateClass}
            onCancel={() => { setShowEditModal(false); resetForm(); }}
            submitting={submitting}
            isEdit
          />
        </DialogContent>
      </Dialog>

      {/* Participants Modal */}
      <Dialog open={showParticipantsModal} onOpenChange={setShowParticipantsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {selectedClass?.title} - Participants
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {participants.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No participants yet</p>
              </div>
            ) : (
              participants.map((participant, index) => (
                <div 
                  key={participant.user_id || index}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {participant.user_name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {participant.user_email}
                    </p>
                  </div>
                  <div className="text-xs text-gray-400">
                    Joined {format(parseISO(participant.joined_at), 'MMM d, HH:mm')}
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowParticipantsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Class Confirmation Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Cancel Group Class
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-300">
                <strong>Warning:</strong> This action cannot be undone.
              </AlertDescription>
            </Alert>
            <p className="text-gray-600 dark:text-gray-400 mt-4">
              Are you sure you want to cancel this group class?
            </p>
            <ul className="mt-3 text-sm text-gray-500 dark:text-gray-400 space-y-1">
              <li>• The class will no longer be visible to the public</li>
              <li>• Participants cannot join this class</li>
              <li>• If cancelled 30+ minutes before start, participants will be notified via email</li>
            </ul>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowCancelModal(false); setClassToCancelId(null); }}
              disabled={cancellingClass}
            >
              Keep Class
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelClass}
              disabled={cancellingClass}
            >
              {cancellingClass ? 'Cancelling...' : 'Yes, Cancel Class'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Active Class Warning Modal */}
      <Dialog open={showActiveClassWarning} onOpenChange={setShowActiveClassWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Active Class Exists
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You already have an active group class. You can only have one active class at a time.
            </p>
            
            {activeClassInfo && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  {activeClassInfo.title}
                </h4>
                <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
                  <p className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    {formatDateTime(activeClassInfo.scheduled_date, activeClassInfo.start_time, activeClassInfo.trainer_timezone)}
                  </p>
                  <p className="flex items-center gap-2">
                    <Badge className={activeClassInfo.status === 'live' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                      {activeClassInfo.status.toUpperCase()}
                    </Badge>
                  </p>
                </div>
              </div>
            )}
            
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
              To create a new class, please:
            </p>
            <ul className="text-sm text-gray-500 dark:text-gray-400 mt-2 space-y-1">
              <li>• Cancel your current class, or</li>
              <li>• Wait for your current class to complete</li>
            </ul>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setShowActiveClassWarning(false); setActiveClassInfo(null); }}
            >
              Close
            </Button>
            <Button 
              onClick={() => { 
                setShowActiveClassWarning(false); 
                setActiveClassInfo(null);
                // Scroll to the active class in the list
                setFilterStatus('upcoming');
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              View Current Class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupClassesTab;
